'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

import { useShallow } from 'zustand/react/shallow'

import { useAvatarImage } from '@common/components/Header/hooks'
import { shouldShowProfileAvatar } from '@common/components/Header/hooks/utils/shouldShowProfileAvatar'
import { AppVariant } from '@common/enums'
import { useAuthUser } from '@common/hooks/useAuthUser'

import { DEFAULT_WORKSPACE_NAME } from '@apps/onboarding/onboarding.const'
import type { Project } from '@modules/projects/projects.type'
import { selectProjectsShellState } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { selectCurrentUserAvatarUrl, selectCurrentUserDisplayData } from '@modules/user/stores/user.selector'
import { useUserStore } from '@modules/user/stores/user.store'
import { selectWorkspacesPickerState } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'

import { useOptionalLayoutMobileMenuNavigation } from '../../Layout/context/LayoutMobileMenuNavigationContext'
import { getInitials } from '../../Sidebar/Sidebar.helpers'
import type { BaseMobileMenuProps } from '../BaseMobileMenu.types'
import type { MenuView, MobileMenuContextValue } from '../context'

export function useBaseMobileMenu({
  isOpen,
  onClose,
  appVariant,
  helpItems,
  menuGroups,
  navigationTopSlot,
  renderHeader,
  onOpenWorkspaceSettings: onOpenWorkspaceSettingsProp,
  onOpenProjectSettings: onOpenProjectSettingsProp,
  onOpenProjectMembers: onOpenProjectMembersProp,
  onInviteProjectClick: onInviteProjectClickProp,
  onInviteWorkspaceClick: onInviteWorkspaceClickProp,
  onCreateProject: onCreateProjectProp,
  onProfileClick: onProfileClickProp,
  onHelpClick: onHelpClickProp,
  onNavigationClick: onNavigationClickProp,
  onProjectClick: onProjectClickProp,
  onWorkspaceClick: onWorkspaceClickProp,
  notificationBadge: notificationBadgeProp,
  onNotificationClick: onNotificationClickProp,
  showProjectSelector = true,
  showWorkspaceSelector = true,
}: BaseMobileMenuProps): MobileMenuContextValue {
  const layoutNavigation = useOptionalLayoutMobileMenuNavigation()
  const onOpenWorkspaceSettings = onOpenWorkspaceSettingsProp ?? layoutNavigation?.handleOpenWorkspaceSettings
  const onOpenProjectSettings = onOpenProjectSettingsProp ?? layoutNavigation?.handleOpenProjectSettings
  const onOpenProjectMembers = onOpenProjectMembersProp ?? layoutNavigation?.handleOpenProjectMembers
  const onInviteProjectClick = onInviteProjectClickProp ?? layoutNavigation?.handleInviteProjectClick
  const onInviteWorkspaceClick = onInviteWorkspaceClickProp ?? layoutNavigation?.handleInviteWorkspaceClick
  const onCreateProject = onCreateProjectProp ?? layoutNavigation?.handleAddProjectClick
  const onProfileClick = onProfileClickProp ?? layoutNavigation?.handleProfileClick
  const onHelpClick = onHelpClickProp ?? layoutNavigation?.handleHelpClick
  const onNavigationClick = onNavigationClickProp ?? layoutNavigation?.handleNavigationClick
  const onProjectClick = onProjectClickProp ?? layoutNavigation?.handleProjectSelect
  const onWorkspaceClick = onWorkspaceClickProp ?? layoutNavigation?.handleWorkspaceSelect
  const notificationBadge = notificationBadgeProp ?? layoutNavigation?.notificationBadge
  const onNotificationClick = onNotificationClickProp ?? layoutNavigation?.onNotificationClick
  const pathname = usePathname()
  const { user: auth0User, isLoading: isAuth0Loading } = useAuthUser()
  const userLoading = useUserStore((state) => state.loading)
  const { fullName, email } = useUserStore(useShallow(selectCurrentUserDisplayData))
  const { projects, currentProject } = useProjectsStore(useShallow(selectProjectsShellState))
  const mobileMenuProjects = projects as Project[]
  const { workspaces, currentWorkspace } = useWorkspacesStore(useShallow(selectWorkspacesPickerState))

  const [currentView, setCurrentView] = useState<MenuView>('main')
  const [isAppPickerOpen, setIsAppPickerOpen] = useState(false)
  const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false)
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false)
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const showHelpSelector = !!helpItems && helpItems.length > 0
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onClose()
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCurrentView('main')
        setIsScrolled(false)
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen])

  useEffect(() => {
    const { body, documentElement } = document
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = documentElement.style.overflow

    if (isOpen) {
      body.style.overflow = 'hidden'
      documentElement.style.overflow = 'hidden'
    } else {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflow = previousHtmlOverflow
    }

    return () => {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  useEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement) return

    const onScroll = () => setIsScrolled(contentElement.scrollTop > 0)

    contentElement.addEventListener('scroll', onScroll, { passive: true })
    return () => contentElement.removeEventListener('scroll', onScroll)
  }, [])

  const displayUserName = auth0User?.name || fullName
  const displayUserEmail = auth0User?.email || email
  const displayProjectName = currentProject?.name || 'Select Project'
  const displayWorkspaceName = currentWorkspace?.name || DEFAULT_WORKSPACE_NAME
  const displayWorkspaceInitials = getInitials(displayWorkspaceName)

  const uploadedAvatarUrl = useUserStore(selectCurrentUserAvatarUrl)
  const avatarSourceUrl = uploadedAvatarUrl ?? auth0User?.picture ?? undefined
  const { src: userAvatarUrl, isImagePending: isUserAvatarImagePending } = useAvatarImage(avatarSourceUrl)
  const showUserAvatar = shouldShowProfileAvatar({
    isSessionLoading: isAuth0Loading || userLoading,
    avatarSourceUrl,
    isImagePending: isUserAvatarImagePending,
  })
  const isModulesMenu = appVariant === AppVariant.MODULES
  const viewClassName = `view${currentView.charAt(0).toUpperCase() + currentView.slice(1)}`

  return useMemo(
    () => ({
      appVariant,
      auth0User,
      contentRef,
      currentProject,
      currentView,
      currentWorkspace,
      displayProjectName,
      displayUserEmail,
      displayUserName,
      displayWorkspaceInitials,
      displayWorkspaceName,
      helpItems,
      isAccountDrawerOpen,
      isAppPickerOpen,
      isHelpDrawerOpen,
      isModulesMenu,
      isOpen,
      isProjectPickerOpen,
      isScrolled,
      menuGroups,
      mobileMenuProjects,
      navigateToMain: () => setCurrentView('main'),
      navigateToProjects: () => setCurrentView('projects'),
      navigateToWorkspaces: () => setCurrentView('workspaces'),
      navigationTopSlot,
      notificationBadge,
      onClose,
      onCreateProject,
      onHelpClick,
      onInviteProjectClick,
      onInviteWorkspaceClick,
      onNavigationClick,
      onNotificationClick,
      onOpenProjectMembers,
      onOpenProjectSettings,
      onOpenWorkspaceSettings,
      onProfileClick,
      onProjectClick,
      onWorkspaceClick,
      renderHeader,
      setCurrentView,
      setIsAccountDrawerOpen,
      setIsAppPickerOpen,
      setIsHelpDrawerOpen,
      setIsProjectPickerOpen,
      showHelpSelector,
      showProjectSelector,
      showWorkspaceSelector,
      showUserAvatar,
      userAvatarUrl,
      viewClassName,
      workspaces,
    }),
    [
      appVariant,
      auth0User,
      currentProject,
      currentView,
      currentWorkspace,
      displayProjectName,
      displayUserEmail,
      displayUserName,
      displayWorkspaceInitials,
      displayWorkspaceName,
      helpItems,
      isAccountDrawerOpen,
      isAppPickerOpen,
      isHelpDrawerOpen,
      isModulesMenu,
      isOpen,
      isProjectPickerOpen,
      isScrolled,
      menuGroups,
      mobileMenuProjects,
      navigationTopSlot,
      notificationBadge,
      onClose,
      onCreateProject,
      onHelpClick,
      onInviteProjectClick,
      onInviteWorkspaceClick,
      onNavigationClick,
      onNotificationClick,
      onOpenProjectMembers,
      onOpenProjectSettings,
      onOpenWorkspaceSettings,
      onProfileClick,
      onProjectClick,
      onWorkspaceClick,
      renderHeader,
      showHelpSelector,
      showProjectSelector,
      showWorkspaceSelector,
      showUserAvatar,
      userAvatarUrl,
      viewClassName,
      workspaces,
    ]
  )
}
